/**
 * Per-make settings (#2201).
 *
 * The make itself is not editable — it exists because vehicles carry it — so
 * this dialog only writes the settings hanging off it, and saving is an upsert.
 *
 * The service-book year range is the point of the whole feature: a manufacturer
 * portal covers model years from some point on, and an older car is not in it
 * at all. Clearing the URL therefore clears the range with it, since a range on
 * its own describes nothing (the API rejects that combination too).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  DialogContent,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ConfirmDialog, FormDialog, VehicleMakeLogo } from '@/components/common';
import { useNotify } from '@/hooks/useNotify';
import { useSuppliers } from '@/hooks/useInventory';
import { describeApiError } from '@/lib/describeApiError';
import {
  useSaveVehicleMakeSettings,
  useDeleteVehicleMakeSettings,
  type VehicleMake,
  type VehicleMakeSettingsInput,
} from '@/hooks/useVehicles';

interface VehicleMakeSettingsDialogProps {
  open: boolean;
  make: VehicleMake | null;
  onClose: () => void;
}

/** Form state is all strings: an empty field and a zero must stay distinct. */
interface FormState {
  serviceBookName: string;
  serviceBookUrl: string;
  serviceBookYearFrom: string;
  serviceBookYearTo: string;
  serviceIntervalKm: string;
  serviceIntervalMonths: string;
  defaultSupplierId: string | null;
  partNumberFormat: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  serviceBookName: '',
  serviceBookUrl: '',
  serviceBookYearFrom: '',
  serviceBookYearTo: '',
  serviceIntervalKm: '',
  serviceIntervalMonths: '',
  defaultSupplierId: null,
  partNumberFormat: '',
  notes: '',
};

function toForm(make: VehicleMake | null): FormState {
  const settings = make?.settings;
  if (settings == null) return EMPTY_FORM;
  return {
    serviceBookName: settings.serviceBookName ?? '',
    serviceBookUrl: settings.serviceBookUrl ?? '',
    serviceBookYearFrom: settings.serviceBookYearFrom?.toString() ?? '',
    serviceBookYearTo: settings.serviceBookYearTo?.toString() ?? '',
    serviceIntervalKm: settings.serviceIntervalKm?.toString() ?? '',
    serviceIntervalMonths: settings.serviceIntervalMonths?.toString() ?? '',
    defaultSupplierId: settings.defaultSupplierId,
    partNumberFormat: settings.partNumberFormat ?? '',
    notes: settings.notes ?? '',
  };
}

/** Empty string means "not set"; the API takes null for that. */
function text(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function number(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function VehicleMakeSettingsDialog({
  open,
  make,
  onClose,
}: VehicleMakeSettingsDialogProps) {
  const { t } = useTranslation('vehicles');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const initial = useMemo(() => toForm(make), [make]);
  useEffect(() => {
    if (open) {
      setForm(initial);
      setError(null);
    }
  }, [open, initial]);

  const { data: supplierPages } = useSuppliers({ isActive: true });
  const suppliers = useMemo(
    () => supplierPages?.pages.flatMap((page) => page.data) ?? [],
    [supplierPages],
  );
  const selectedSupplier =
    suppliers.find((supplier) => supplier.id === form.defaultSupplierId) ?? null;

  const saveMutation = useSaveVehicleMakeSettings();
  const deleteMutation = useDeleteVehicleMakeSettings();

  const isDirty = useMemo(
    () => (Object.keys(form) as (keyof FormState)[]).some((key) => form[key] !== initial[key]),
    [form, initial],
  );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError(null);
  };

  const handleSave = () => {
    if (make === null) return;

    const yearFrom = number(form.serviceBookYearFrom);
    const yearTo = number(form.serviceBookYearTo);
    const url = text(form.serviceBookUrl);

    if (url === null && (yearFrom !== null || yearTo !== null)) {
      setError(t('makes.dialog.yearsNeedUrl'));
      return;
    }
    if (yearFrom !== null && yearTo !== null && yearFrom > yearTo) {
      setError(t('makes.dialog.yearOrder'));
      return;
    }
    if (url !== null && !/^https?:\/\//.test(url)) {
      setError(t('makes.dialog.urlProtocol'));
      return;
    }

    const settings: VehicleMakeSettingsInput = {
      serviceBookName: text(form.serviceBookName),
      serviceBookUrl: url,
      serviceBookYearFrom: yearFrom,
      serviceBookYearTo: yearTo,
      serviceIntervalKm: number(form.serviceIntervalKm),
      serviceIntervalMonths: number(form.serviceIntervalMonths),
      defaultSupplierId: form.defaultSupplierId,
      partNumberFormat: text(form.partNumberFormat),
      notes: text(form.notes),
    };

    saveMutation.mutate(
      { make: make.make, settings },
      {
        onSuccess: () => {
          notify.success(t('makes.dialog.saved'), t('makes.dialog.savedDesc', { make: make.make }));
          onClose();
        },
        onError: (err) => {
          setError(describeApiError(err, tc, { fallback: tc('errors.generic') }));
        },
      },
    );
  };

  const handleClear = () => {
    if (make === null || make.settings === null) return;
    deleteMutation.mutate(make.make, {
      onSuccess: () => {
        notify.success(
          t('makes.dialog.cleared'),
          t('makes.dialog.clearedDesc', { make: make.make }),
        );
        onClose();
      },
      onError: (err) => {
        setError(describeApiError(err, tc, { fallback: tc('errors.generic') }));
      },
    });
  };

  if (make === null) return null;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={make.make}
      headerIcon={<VehicleMakeLogo make={make.make} size={32} />}
      isDirty={isDirty}
      onSubmit={handleSave}
      submitLabel={tc('save')}
      isLoading={saveMutation.isPending || deleteMutation.isPending}
      maxWidth="sm"
    >
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('makes.dialog.vehicleSummary', {
            count: make.vehicleCount,
            models: make.models.length,
          })}
        </Typography>

        {error !== null && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('makes.dialog.serviceBookTitle')}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label={t('makes.dialog.serviceBookName')}
              value={form.serviceBookName}
              onChange={(e) => { set('serviceBookName', e.target.value); }}
              fullWidth
            />
            <TextField
              label={t('makes.dialog.serviceBookUrl')}
              value={form.serviceBookUrl}
              onChange={(e) => { set('serviceBookUrl', e.target.value); }}
              placeholder="https://"
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label={t('makes.dialog.yearFrom')}
                value={form.serviceBookYearFrom}
                onChange={(e) => { set('serviceBookYearFrom', e.target.value); }}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                fullWidth
              />
              <TextField
                label={t('makes.dialog.yearTo')}
                value={form.serviceBookYearTo}
                onChange={(e) => { set('serviceBookYearTo', e.target.value); }}
                helperText={t('makes.dialog.yearToHint')}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                fullWidth
              />
            </Stack>
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('makes.dialog.serviceIntervalTitle')}
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label={t('makes.dialog.intervalKm')}
              value={form.serviceIntervalKm}
              onChange={(e) => { set('serviceIntervalKm', e.target.value); }}
              slotProps={{
                htmlInput: { inputMode: 'numeric' },
                input: { endAdornment: <InputAdornment position="end">km</InputAdornment> },
              }}
              fullWidth
            />
            <TextField
              label={t('makes.dialog.intervalMonths')}
              value={form.serviceIntervalMonths}
              onChange={(e) => { set('serviceIntervalMonths', e.target.value); }}
              helperText={t('makes.dialog.intervalHint')}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              fullWidth
            />
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('makes.dialog.partsTitle')}
          </Typography>
          <Stack spacing={2}>
            <Autocomplete
              options={suppliers}
              value={selectedSupplier}
              onChange={(_, value) => { set('defaultSupplierId', value?.id ?? null); }}
              getOptionLabel={(supplier) => supplier.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label={t('makes.dialog.defaultSupplier')} />
              )}
              fullWidth
            />
            <TextField
              label={t('makes.dialog.partNumberFormat')}
              value={form.partNumberFormat}
              onChange={(e) => { set('partNumberFormat', e.target.value); }}
              helperText={t('makes.dialog.partNumberFormatHint')}
              fullWidth
            />
          </Stack>
        </Box>

        <Divider />

        <TextField
          label={t('makes.dialog.notes')}
          value={form.notes}
          onChange={(e) => { set('notes', e.target.value); }}
          multiline
          minRows={2}
          // Grows with the text, then scrolls: a long note should not stretch
          // the dialog past the save button.
          maxRows={8}
          fullWidth
        />

        {make.settings !== null && (
          <Box>
            <Button
              color="error"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => { setConfirmClear(true); }}
            >
              {t('makes.dialog.clear')}
            </Button>
          </Box>
        )}
        </Stack>
      </DialogContent>

      {/* Clears the settings, never the make — it stays in the list as long as
          a vehicle carries it. */}
      <ConfirmDialog
        open={confirmClear}
        title={t('makes.dialog.clearConfirmTitle')}
        message={t('makes.dialog.clearConfirmMessage', { make: make.make })}
        confirmText={tc('delete')}
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          setConfirmClear(false);
          handleClear();
        }}
        onCancel={() => { setConfirmClear(false); }}
      />
    </FormDialog>
  );
}
